import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchData } from '../hooks/useDataRefresh';
import { useApi } from '../hooks/useApi';

// Deranged SKUs excluded by EAN (S&V products)
const DERANGED_EANS = new Set(['9316401202914']);

function pct(v) {
  if (v == null) return '—';
  return `${(v * 100).toFixed(0)}%`;
}

function DistBar({ value, color }) {
  if (value == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const pctVal = Math.min(value * 100, 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3 }}>
        <div style={{ width: `${pctVal}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-primary)', width: 36, textAlign: 'right' }}>{pct(value)}</span>
    </div>
  );
}

export default function Distribution() {
  const [data, setData] = useState([]);
  const [ytd, setYTD] = useState(null);
  const [brandFilter, setBrandFilter] = useState('All');

  useEffect(() => {
    Promise.all([
      fetchData('scanv2-distribution.json'),
      fetchData('scanv2-ytd.json'),
    ]).then(([d, y]) => {
      setData(d.filter(sku => !DERANGED_EANS.has(sku.ean)));
      setYTD(y);
    });
  }, []);

  // Data vintage from JsonStore's UpdatedAt (#2 lineage: blob UpdatedAt = the
  // data's "as of" time), so the label tracks the data on refresh instead of a
  // hardcoded date that silently drifts out of step with the scan feed.
  const apiFetch = useApi();
  const { data: freshness } = useQuery({
    queryKey: ['data-freshness'],
    queryFn: async () => (await apiFetch('/data-freshness')).json(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const distRow = freshness?.find(r => r.key === 'scanv2-distribution');
  const asOf = distRow
    ? new Date(distRow.updatedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  const families = ['All', ...new Set(data.map(d => d.family))];
  const filtered = brandFilter === 'All' ? data : data.filter(d => d.family === brandFilter);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary KPIs */}
      <div style={{ display: 'flex', gap: 12 }}>
        {[
          { label: 'WW SKUs Ranged', value: ytd?.ww_ranged, color: '#1a8a3f' },
          { label: 'WW SKUs Selling', value: ytd?.ww_selling, color: '#1a8a3f' },
          { label: 'Coles SKUs Ranged', value: ytd?.coles_ranged, color: '#e03131' },
          { label: 'Coles SKUs Selling', value: ytd?.coles_selling, color: '#e03131' },
        ].map(k => (
          <div key={k.label} className="kpi-card" style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: k.color }}>{k.value ?? '—'}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>SKUs{asOf ? ` — as of ${asOf}` : ''}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Brand</span>
        {families.map(f => (
          <button key={f} className={brandFilter === f ? 'pill-active' : 'pill-inactive'} onClick={() => setBrandFilter(f)}>{f}</button>
        ))}
      </div>

      {/* Distribution Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>SKU Distribution</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{asOf ? `As of ${asOf}` : 'Latest scan week'}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Family</th>
                <th>WW Ranged %</th>
                <th>WW Selling %</th>
                <th>Coles Ranged %</th>
                <th>Coles Selling %</th>
              </tr>
            </thead>
            <tbody>
              {filtered
                .filter(d => (d.ww_ranged > 0.05 || d.coles_ranged > 0.05 || d.ww_selling > 0.05 || d.coles_selling > 0.05))
                .map(d => (
                  <tr key={d.ean}>
                    <td style={{ color: 'var(--text-primary)' }}>
                      {d.name.replace(/\d+ x /g, '').replace(/^(Bhuja|Infuzions) /, '')}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.name}</div>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--border)', borderRadius: 4, padding: '2px 6px' }}>
                        {d.family.replace('Infuzions Multipack', 'Infuzions MP')}
                      </span>
                    </td>
                    <td style={{ minWidth: 120 }}>
                      <DistBar value={d.ww_ranged} color="#1a8a3f" />
                    </td>
                    <td style={{ minWidth: 120 }}>
                      <DistBar value={d.ww_selling} color="#1a8a3f" />
                    </td>
                    <td style={{ minWidth: 120 }}>
                      <DistBar value={d.coles_ranged} color="#e03131" />
                    </td>
                    <td style={{ minWidth: 120 }}>
                      <DistBar value={d.coles_selling} color="#e03131" />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
